import React from 'react';
import { mapEmbedSrc, mapLinkHref, site, telHref } from './content';
import './site.css';

/**
 * The public face of the restaurant, served at `/`.
 *
 * The manager app used to be the whole of the web build, so typing the address
 * put a customer straight into a staff login screen — a page that can do
 * nothing for them and rather suggests they are in the wrong place. The app now
 * lives at `/app`, and this stands in front of it.
 *
 * Nothing here talks to Supabase. It is a static page by design: it must render
 * for somebody with no account, and it has no reason to be slower than a
 * document.
 */
export function LandingPage() {
  return (
    <div className="site">
      <a className="skip" href="#main">
        Skip to content
      </a>

      <header className="site-header">
        <div className="wrap">
          <a className="brand" href="/">
            <img src="/icon-192.png" alt="" width={38} height={38} />
            <span>
              <span className="brand-name">{site.name}</span>
              <br />
              <span className="brand-sub">Katargam, Surat</span>
            </span>
          </a>

          <nav className="nav-links" aria-label="Sections">
            <a href="#menu">Menu</a>
            <a href="#reviews">Reviews</a>
            <a href="#visit">Visit</a>
          </nav>

          {/* The one door into the till. Staff only, and said so, because
              everybody else arriving here is a customer. */}
          <a className="btn btn-ghost" href="/app">
            Staff sign in
          </a>
        </div>
      </header>

      <main id="main">
        <section className="hero">
          <div className="wrap hero-inner">
            <div>
              <p className="eyebrow">Family run · Since day one</p>
              <h1>{site.name}</h1>
              <p className="hero-lede">{site.tagline}</p>
              <div className="hero-actions">
                <a className="btn btn-primary" href={telHref}>
                  Call {site.phone}
                </a>
                <a
                  className="btn btn-ghost"
                  href={mapLinkHref}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Get directions
                </a>
              </div>
            </div>

            <aside className="hero-card">
              <h2 className="serif">Today</h2>
              {site.hours.map((h) => (
                <div className="fact" key={h.days}>
                  <span className="fact-label">{h.days}</span>
                  <span className="fact-value">{h.time}</span>
                </div>
              ))}
              <div className="fact">
                <span className="fact-label">Phone</span>
                <span className="fact-value">
                  <a href={telHref}>{site.phone}</a>
                </span>
              </div>
              <div className="fact">
                <span className="fact-label">Find us</span>
                <span className="fact-value">{site.address}</span>
              </div>
            </aside>
          </div>
        </section>

        <section className="section" id="menu">
          <div className="wrap">
            <div className="section-head">
              <h2>What we cook</h2>
              <p>
                Everything is made to order. These are the sections of the menu — ask
                at the counter for the day's specials.
              </p>
            </div>
            <div className="menu-grid">
              {site.menu.map((m) => (
                <article className="menu-card" key={m.name}>
                  <h3>{m.name}</h3>
                  <p>{m.note}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="reviews">
          <div className="wrap">
            <div className="section-head">
              <h2>What people say</h2>
              <p>
                <strong>{site.rating.score} ★</strong> from {site.rating.count} reviews on
                {' '}
                <a href={mapLinkHref} target="_blank" rel="noreferrer noopener">
                  Google
                </a>
                .
              </p>
            </div>
            <div className="quotes">
              {site.testimonials.map((q, i) => (
                <figure className="quote" key={i}>
                  <blockquote>“{q.quote}”</blockquote>
                  <figcaption>
                    {q.author ? <b>{q.author}</b> : null}
                    {q.author ? ' · ' : null}
                    {q.where}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="visit">
          <div className="wrap">
            <div className="section-head">
              <h2>Come and find us</h2>
              <p>{site.address}</p>
            </div>
            <div className="visit">
              <div>
                <div className="fact">
                  <span className="fact-label">Hours</span>
                  <span className="fact-value">
                    {site.hours.map((h) => (
                      <span key={h.days}>
                        {h.days}
                        <br />
                        {h.time}
                        <br />
                        <br />
                      </span>
                    ))}
                  </span>
                </div>
                <div className="fact">
                  <span className="fact-label">Phone</span>
                  <span className="fact-value">
                    <a href={telHref}>{site.phone}</a>
                  </span>
                </div>
                {site.email ? (
                  <div className="fact">
                    <span className="fact-label">Email</span>
                    <span className="fact-value">
                      <a href={`mailto:${site.email}`}>{site.email}</a>
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Google's embed takes a plain query, so there is no API key to
                  leak and nothing to bill. loading="lazy" keeps a third-party
                  frame off the critical path. */}
              <div className="map-frame">
                <iframe
                  src={mapEmbedSrc}
                  title={`Map showing ${site.name}`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="wrap">
          <span>
            © {new Date().getFullYear()} {site.name}
          </span>
          <span className="spacer" />
          <a href={mapLinkHref} target="_blank" rel="noreferrer noopener">
            Directions
          </a>
          <a href={telHref}>Call</a>
          <a href="/app">Staff sign in</a>
        </div>
      </footer>
    </div>
  );
}
